import { useEffect, useState } from "react";
import {
  Autocomplete,
  createFilterOptions,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import {
  AppButton,
  FilterBar,
  FilterDrawer,
} from "../../../../../components/ui";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  buildActiveChips,
  clearFilterField,
  countActiveFilters,
  hasActiveFilters,
  resetFilters,
} from "./filtering";
import {
  FILTER_FIELD_LABELS,
  YES_NO_OPTIONS,
  type AttorneysFiltersState,
} from "./types";

type PracticeAreaOption = {
  value: string;
  label: string;
};

type AttorneysTableFiltersProps = {
  filters: AttorneysFiltersState;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onApply: (next: AttorneysFiltersState) => void;
  onClear: () => void;
  practiceAreaOptions: PracticeAreaOption[];
  emailDomainOptions: string[];
};

const ALL_OPTION = "All";

const stringFilterOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (option) => option,
  trim: true,
  ignoreCase: true,
});

function ValueAutocomplete({
  label,
  value,
  options,
  onChange,
  placeholder = "Type to filter…",
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  return (
    <Autocomplete
      size="small"
      options={[{ value: "", label: ALL_OPTION }, ...options]}
      value={
        options.find((o) => o.value === value) ?? {
          value: "",
          label: ALL_OPTION,
        }
      }
      onChange={(_, next) => onChange(!next || !next.value ? "" : next.value)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      autoHighlight
      clearOnBlur={false}
      handleHomeEndKeys
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
      fullWidth
    />
  );
}

export function AttorneysTableFilters({
  filters,
  searchValue = "",
  onSearchChange,
  onApply,
  onClear,
  practiceAreaOptions,
  emailDomainOptions,
}: AttorneysTableFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<AttorneysFiltersState>(filters);
  const filtersActive = hasActiveFilters(filters);
  const draftActive = hasActiveFilters(draft);
  const activeCount = countActiveFilters(filters);
  const formatPracticeArea = (value: string) =>
    practiceAreaOptions.find((o) => o.value === value)?.label ?? value;
  const activeChips = buildActiveChips(filters, formatPracticeArea);

  useEffect(() => {
    if (drawerOpen) {
      setDraft(filters);
    }
  }, [drawerOpen, filters]);

  const patchDraft = (patch: Partial<AttorneysFiltersState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const clearField = (key: keyof AttorneysFiltersState) => {
    onApply(clearFilterField(filters, key));
  };

  const handleApply = () => {
    onApply(draft);
    setDrawerOpen(false);
  };

  const clearDraft = () => setDraft(resetFilters());

  const handleClearAll = () => {
    onClear();
    onSearchChange?.("");
  };

  return (
    <>
      <FilterBar
        activeCount={activeCount}
        filtersActive={filtersActive}
        onOpenFilters={() => setDrawerOpen(true)}
        onClear={handleClearAll}
        clearDisabled={!filtersActive && !searchValue}
        chips={activeChips.map((chip) => ({
          key: String(chip.key),
          label: chip.label,
          onDelete: () => clearField(chip.key),
        }))}
        leading={
          <TextField
            size="small"
            placeholder="Search by name, email, company, or practice area…"
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 320 },
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          />
        }
      />

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        subtitle="Apply to refresh the attorney directory with these filters"
        activeCount={activeCount}
        actions={
          <>
            <AppButton
              variant="outlined"
              color="inherit"
              fullWidth
              startIcon={<FilterAltOffIcon />}
              onClick={clearDraft}
              disabled={!draftActive}
            >
              Clear filters
            </AppButton>
            <AppButton variant="contained" fullWidth onClick={handleApply}>
              Apply
            </AppButton>
          </>
        }
      >
        <Stack spacing={1.75}>
          <ValueAutocomplete
            label={FILTER_FIELD_LABELS.practiceArea}
            value={draft.practiceArea}
            options={practiceAreaOptions}
            onChange={(practiceArea) => patchDraft({ practiceArea })}
          />

          <Autocomplete
            size="small"
            options={[ALL_OPTION, ...emailDomainOptions]}
            value={draft.emailDomain || ALL_OPTION}
            onChange={(_, next) => {
              if (!next || next === ALL_OPTION) {
                patchDraft({ emailDomain: "" });
                return;
              }
              patchDraft({ emailDomain: next });
            }}
            filterOptions={stringFilterOptions}
            autoHighlight
            clearOnBlur={false}
            handleHomeEndKeys
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={FILTER_FIELD_LABELS.emailDomain}
                placeholder={
                  emailDomainOptions.length
                    ? "Type to filter…"
                    : "No domains found"
                }
              />
            )}
            fullWidth
          />

          <ValueAutocomplete
            label={FILTER_FIELD_LABELS.hasCompany}
            value={draft.hasCompany}
            options={[...YES_NO_OPTIONS]}
            onChange={(hasCompany) => patchDraft({ hasCompany })}
          />
        </Stack>
      </FilterDrawer>
    </>
  );
}
